import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientDto,
  CreateClientInput,
  CreateSubscriptionInput,
  PlanDto,
  SubscriptionDto,
} from '@cpm/shared';
import { ArrowLeft, Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { ClientLoginManagement } from '../components/ClientLoginManagement.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  statusTone,
} from '../components/ui.js';
import { ClientForm } from './Clients.js';

interface ClientDetailResponse {
  client: ClientDto;
  subscriptions: SubscriptionDto[];
}

function NewSubscriptionForm({
  clientId,
  onDone,
}: {
  clientId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanDto[]>('/api/plans/'),
  });
  const [name, setName] = useState('');
  const [planId, setPlanId] = useState('');
  const [createProject, setCreateProject] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (input: CreateSubscriptionInput) =>
      api.post<{ subscription: SubscriptionDto; warning: string | null }>(
        '/api/subscriptions/',
        input,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      if (result.warning) {
        setWarning(result.warning);
      } else {
        onDone();
        void navigate(`/subscriptions/${result.subscription.id}`);
      }
    },
  });

  const activePlans = (plans.data ?? []).filter((p) => p.isActive);

  if (warning) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
          {warning}
        </div>
        <div className="flex justify-end">
          <Button onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({ clientId, planId, name, createCoolifyProject: createProject });
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="sub-name">Subscription name</Label>
        <Input
          id="sub-name"
          required
          placeholder="e.g. Main website"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="sub-plan">Service plan</Label>
        <Select id="sub-plan" required value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="" disabled>
            {plans.isPending ? 'Loading plans…' : 'Select a plan'}
          </option>
          {activePlans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </Select>
        {activePlans.length === 0 && !plans.isPending && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            No active plans.{' '}
            <Link to="/plans" className="underline">
              Create one first.
            </Link>
          </p>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
          checked={createProject}
          onChange={(e) => setCreateProject(e.target.checked)}
        />
        Create a dedicated Coolify project for this subscription
      </label>
      {create.error != null && <ErrorNote error={create.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={create.isPending} disabled={!planId}>
          Create subscription
        </Button>
      </div>
    </form>
  );
}

export function ClientDetailPage() {
  const me = useMe();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [newSubOpen, setNewSubOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get<ClientDetailResponse>(`/api/clients/${id}`),
    enabled: Boolean(id),
  });

  const update = useMutation({
    mutationFn: (input: Partial<CreateClientInput> & { status?: ClientDto['status'] }) =>
      api.patch<ClientDto>(`/api/clients/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      setEditOpen(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/clients/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void navigate('/clients');
    },
  });

  const viewAsClient = useMutation({
    mutationFn: () => api.post(`/api/clients/${id}/impersonate`, {}),
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (query) =>
        query.queryKey[0] !== 'auth' || query.queryKey[1] !== 'me' });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      void navigate('/portal');
    },
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { client, subscriptions } = detail.data;

  return (
    <div>
      <Link
        to="/clients"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> All clients
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {client.name} <Badge tone={statusTone(client.status)}>{client.status}</Badge>
          </span>
        }
        description={client.company ?? undefined}
        actions={
          <>
            {me.data?.role === 'admin' && client.status === 'active' && (
              <Button variant="secondary" loading={viewAsClient.isPending} onClick={() => viewAsClient.mutate()}>
                Log in as client
              </Button>
            )}
            {client.status === 'active' ? (
              <Button variant="secondary" onClick={() => update.mutate({ status: 'suspended' })}>
                Suspend
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => update.mutate({ status: 'active' })}>
                Activate
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
            {subscriptions.length === 0 && (
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) {
                    remove.mutate();
                  }
                }}
              >
                <Trash2 className="size-4" /> Delete
              </Button>
            )}
          </>
        }
      />
      {(update.error ?? remove.error ?? viewAsClient.error) != null && (
        <div className="mb-4">
          <ErrorNote error={update.error ?? remove.error ?? viewAsClient.error} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Contact" />
          <dl className="space-y-3 px-5 py-4 text-sm">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Email</dt>
              <dd className="font-medium">{client.email}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Phone</dt>
              <dd className="font-medium">{client.phone ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Created</dt>
              <dd className="font-medium">{new Date(client.createdAt).toLocaleDateString()}</dd>
            </div>
            {client.notes && (
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Notes</dt>
                <dd className="whitespace-pre-wrap">{client.notes}</dd>
              </div>
            )}
          </dl>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Subscriptions"
            actions={
              <Button onClick={() => setNewSubOpen(true)}>
                <Plus className="size-4" /> New subscription
              </Button>
            }
          />
          {subscriptions.length === 0 ? (
            <EmptyState
              icon={<Package className="size-10" />}
              title="No subscriptions"
              description="Assign a service plan to give this client hosting resources."
            />
          ) : (
            <ul className="divide-y divide-slate-100 px-5 dark:divide-slate-800">
              {subscriptions.map((sub) => (
                <li key={sub.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/subscriptions/${sub.id}`}
                      className="text-sm font-medium hover:text-brand-600"
                    >
                      {sub.name}
                    </Link>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Plan: {sub.planName}
                      {sub.coolifyProjectUuid && ' · Coolify project linked'}
                    </p>
                  </div>
                  <Badge tone={statusTone(sub.status)}>{sub.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {me.data?.role === 'admin' && <ClientLoginManagement client={client} />}

      <Modal title="Edit client" open={editOpen} onClose={() => setEditOpen(false)} wide>
        <ClientForm
          initial={client}
          onSubmit={(input) => update.mutate(input)}
          pending={update.isPending}
          error={update.error}
          submitLabel="Save changes"
        />
      </Modal>

      <Modal title="New subscription" open={newSubOpen} onClose={() => setNewSubOpen(false)}>
        <NewSubscriptionForm clientId={client.id} onDone={() => setNewSubOpen(false)} />
      </Modal>
    </div>
  );
}
